import moment from 'moment';
import {
  MlocPeriods,
  ProviderMatchProviderLocationContactV9,
  ProviderMatchProviderLocationV9
} from '@kyruus/types';
import { IntlShape } from '@kyruus/intl';

import messages from '../../lib/messages';

export const getContactDetails = (
  location: ProviderMatchProviderLocationV9,
  contactType: string
): string | undefined => {
  const contacts = (location.contacts || []).find(
    (item: ProviderMatchProviderLocationContactV9) =>
      item.contact_type === contactType
  );
  return (
    contacts?.value ||
    (location[contactType as keyof typeof location] as string)
  );
};

export const getGoogleMapLink = (
  location: ProviderMatchProviderLocationV9
): string => {
  // The parameter api=1 is required in every request and the only valid value currently is 1.
  // If api=1 is not present in the URL, all parameters are ignored
  // Docs: https://developers.google.com/maps/documentation/urls/get-started#forming-the-url
  const baseMapUrl = 'https://www.google.com/maps/search/?api=1&query=';
  const addressFields = ['street1', 'street2', 'suite', 'city', 'state', 'zip'];
  const destinationAddress = addressFields
    .map((field) => location[field as keyof typeof location])
    .filter((i) => !!i)
    .join(', ');

  const locationName = location.exterior_signage_name || location.name;
  const locationURI = encodeURIComponent(
    locationName ? `${locationName}, ${destinationAddress}` : destinationAddress
  );

  return `${baseMapUrl}${locationURI}`;
};

/** Returns array of strings - weekdays list
 * output - ['Monday', 'Tuesday', ...]
 * @param {string} format i.e 'dddd'
 * */
export const getWeekdays = (format: string): any =>
  [...Array(7).keys()].map(function (_, i) {
    return moment(i, 'e')
      .startOf('week')
      .isoWeekday(i + 1)
      .format(format);
  });

/** Returns current day
 * output - 'Monday'
 * @param {string} format i.e 'dddd'
 * @param {string} timezone - optional IANA timezone (e.g. 'US/Eastern')
 * */
export const getCurrentDay = (format: string, timezone?: string): string => {
  if (timezone) {
    try {
      // Use Intl to get the weekday name in the location's timezone
      const now = new Date();
      const dayName = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        timeZone: timezone
      }).format(now);
      return dayName;
    } catch (e) {
      // Fall back to assuming user timezone matches location timezone if none specified
      const weekdays = getWeekdays(format);
      const currentDayIndex = moment().day();
      return weekdays[(currentDayIndex || weekdays.length) - 1];
    }
  } else {
    const weekdays = getWeekdays(format);
    const currentDayIndex = moment().day();
    return weekdays[(currentDayIndex || weekdays.length) - 1];
  }
};

/** Returns current time formatted as 'HH:mm' in the given timezone
 * @param {string} timezone - optional IANA timezone (e.g. 'US/Eastern')
 * */
export const getCurrentTime = (timezone?: string): string => {
  if (timezone) {
    try {
      const now = new Date();
      const parts = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone
      }).formatToParts(now);
      const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
      const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
      return `${hour}:${minute}`;
    } catch (e) {
      // Fall back to assuming user timezone matches location timezone if none specified
      return moment().format('HH:mm');
    }
  }
  return moment().format('HH:mm');
};

/** Returns sorted mloc periods in given weekdays order
 * output - {
 *     {openDay: Monday, ...},
 *     {openDay: Tuesday, ...},
 *     ....
 * }
 * @param {array} hoursList - provider mlocs hours data
 * @param {string} weekdays - weekdays list ['Monday', 'Tuesday', ...]
 * */
export const sortHoursListByWeekdaysOrder = (
  hoursList: MlocPeriods[],
  weekdays: string[]
): any => {
  const ordering: any = {};
  for (let i = 0; i < weekdays.length; i++) ordering[weekdays[i]] = i;
  return hoursList.sort(function (a: any, b: any) {
    return (
      ordering[a.openDay] - ordering[b.openDay] ||
      a.openTime.localeCompare(b.openTime)
    );
  });
};

interface IsClinicOpenProps {
  isOpenNow:
    | {
        closeDay: string;
        closeTime: string;
        openDay: string;
        openTime: string;
      }
    | undefined;
  willOpenNextToday:
    | {
        closeDay: string;
        closeTime: string;
        openDay: string;
        openTime: string;
      }
    | undefined;
}

/** Returns object - find availability in mloc data based on current day/time
 * output - {
 *     isOpenNow: {} | undefined,
 *     willOpenNextToday: {} | undefined,
 * }
 * @param {array} data - provider mlocs hours data
 * @param {string} time - current time
 * */
export const isClinicOpen = (data: [], time: string): IsClinicOpenProps => {
  const isOpenNow = data.find(
    (t: any) => time >= t.openTime && time <= t.closeTime
  );
  const willOpenNextToday = data.find((t: any) => time < t.openTime);
  return {
    isOpenNow,
    willOpenNextToday
  };
};

const convert24To12HoursFormat = (format: string) =>
  moment(format, 'HH:mm').format('hh:mm A');

/** Returns Array of strings
 * output - ['Closed', 'Open Next {available time slot}']
 * @param {array} hoursList - provider mlocs hours data
 * @param {array} weekdaysList - weekdays list ['Monday', 'Tuesday', ...]
 * */
const getNextDayAvailability = (
  hoursList: MlocPeriods[],
  weekdaysList: string[],
  day: string,
  intl: IntlShape
): string[] | undefined => {
  let getNextDay;
  const todayIndex = weekdaysList.indexOf(day) + 1;
  const slicePastDays = weekdaysList.slice(todayIndex, weekdaysList.length);
  getNextDay = hoursList.filter((t: any) => slicePastDays.includes(t.openDay));
  if (!getNextDay.length) {
    getNextDay = hoursList.filter((t: any) => weekdaysList.includes(t.openDay));
  }
  const validGetNextDay =
    Array.isArray(getNextDay) && getNextDay[0] ? getNextDay[0] : null;

  if (!validGetNextDay) {
    return;
  }

  const nextAvailDay = validGetNextDay.openDay;
  const nextAvailTime = convert24To12HoursFormat(
    validGetNextDay.openTime || ''
  );
  return [
    `${intl.formatMessage(messages.closed)}`,
    `${intl.formatMessage(messages.opensNext, {
      nextAvailDay,
      nextAvailTime
    })}`
  ];
};

/** Returns Array of string to be render on client side
 * output - ['Open until {time}'] or ['Closed', 'Open Next {available time slot}']
 * @param {array} periods - provider mlocs hours data
 * @param {string} day - current day
 * @param {string} time - current time
 * */
export const getProviderClinicAvailability = (
  periods: MlocPeriods[],
  day: string,
  time: string,
  intl: IntlShape
): string[] | undefined => {
  // sanitizing data here because we source it from external systems and it may be bad
  const sanitizedPeriods = (() => {
    if (Array.isArray(periods)) {
      return periods.filter((p) => {
        return (
          Boolean(p) &&
          typeof p === 'object' &&
          (Boolean(p.openDay) || Boolean(p.openTime))
        );
      });
    }

    return [];
  })();
  const weekdaysList = getWeekdays('dddd');
  const sortedHoursList = sortHoursListByWeekdaysOrder(
    sanitizedPeriods,
    weekdaysList
  );
  const filterPresentDayObj = sortedHoursList.filter(
    (i: any) => i.openDay === day
  );
  const checkAvailability = isClinicOpen(filterPresentDayObj, time);
  if (checkAvailability.isOpenNow) {
    const openUntilTime = convert24To12HoursFormat(
      checkAvailability.isOpenNow.closeTime
    );
    return [`${intl.formatMessage(messages.openUntil, { openUntilTime })}`];
  }
  if (checkAvailability.willOpenNextToday) {
    const openNextTime = convert24To12HoursFormat(
      checkAvailability.willOpenNextToday.openTime
    );
    return [
      `${intl.formatMessage(messages.closed)}`,
      `${intl.formatMessage(messages.openNextToday, { openNextTime })}`
    ];
  }
  if (!checkAvailability.isOpenNow && !checkAvailability.willOpenNextToday) {
    return getNextDayAvailability(sortedHoursList, weekdaysList, day, intl);
  }

  return;
};
